/**
 * Export, import, share links, version history and entity-level ACL events.
 *
 * One slice of the `ANALYTICS_EVENTS` SSOT — see `../analytics.ts`, which
 * spreads every slice back into the single import surface call sites use.
 * The event NAME STRINGS are a PostHog dashboard contract: add and retire keys
 * here, never rename a value in place (see `rules/analytics.md`).
 */

export const SHARING_EVENTS = {
  // Export
  EXPORT_INITIATED: 'Export Initiated',
  EXPORT_COMPLETED: 'Export Completed',
  EXPORT_FAILED: 'Export Failed',

  // Import
  IMPORT_INITIATED: 'Import Initiated',
  IMPORT_COMPLETED: 'Import Completed',

  // Share Links
  SHARE_LINK_CREATED: 'Share Link Created',
  SHARE_LINK_REVOKED: 'Share Link Revoked',
  SHARE_LINK_VIEWED: 'Share Link Viewed',

  // Version History
  VERSION_HISTORY_OPENED: 'Version History Opened',
  VERSION_SNAPSHOT_PREVIEWED: 'Version Snapshot Previewed',
  VERSION_SNAPSHOT_RESTORED: 'Version Snapshot Restored',
  VERSION_SNAPSHOT_RENAMED: 'Version Snapshot Renamed',
  VERSION_HISTORY_GATE_SHOWN: 'Version History Gate Shown',

  // Entity-level ACL — Share dialog interactions.  Properties always
  // include `entity_type` and `entity_id` so cohort analysis can
  // segment by Project vs Artifact.
  SHARE_DIALOG_OPENED: 'Share Dialog Opened',
  ENTITY_PRIVACY_CHANGED: 'Entity Privacy Changed',
  ENTITY_PRIVACY_RESET: 'Entity Privacy Reset',
  ENTITY_SHARED: 'Entity Shared',
  ENTITY_UNSHARED: 'Entity Unshared',
  ENTITY_PERMISSION_CHANGED: 'Entity Permission Changed',
  ENTITY_MADE_PRIVATE: 'Entity Made Private',
  // Restricted-access splash — fired when a workspace member follows
  // a direct URL to an entity they don't have read access to.  Lets
  // product measure the gap between "users who hit a private link"
  // and "users who click Request access" — a high splash-views,
  // low-CTA ratio would suggest the splash copy isn't landing.
  RESTRICTED_ACCESS_SPLASH_VIEWED: 'Restricted Access Splash Viewed',
  REQUEST_ACCESS_CLICKED: 'Request Access Clicked',
  // Workspace ownership transfer — high-stakes, low-frequency event.
  // Tracked so we can monitor adoption + alert on unexpected spikes.
  // Properties: `workspace_id`, `target_user_id` (no emails / names).
  WORKSPACE_OWNERSHIP_TRANSFERRED: 'Workspace Ownership Transferred',

  // Fired when a sole-member owner deletes their only workspace to unblock
  // account deletion (the transfer dialog's no-other-members escape hatch).
  // Funnel signal: how often the sole-member deletion path is used vs. the
  // multi-member transfer path. Property: `workspace_id` (no emails / names).
  WORKSPACE_DELETED_TO_UNBLOCK_ACCOUNT_DELETION: 'Workspace Deleted To Unblock Account Deletion',

  // The owner scheduled their workspace for deletion (Settings → Workspace),
  // and the owner restored one inside the grace period. Restore rate is the
  // signal that the grace period is earning its keep. Properties:
  // `workspace_id`, plus `billing_cancelled` / `was_last_workspace` on
  // scheduling and `billing_resumed` on restore (no emails / names).
  WORKSPACE_DELETION_SCHEDULED: 'Workspace Deletion Scheduled',
  WORKSPACE_RESTORED: 'Workspace Restored',
} as const;
